import { DateTime } from "luxon";

export function formatTimestamp(
	date: string | null | undefined | number,
): string {
	if (date === null || date === undefined || date === "") {
		return "";
	}

	if (typeof date === "string") {
		// Si es una fecha ISO (contiene T), parseamos y formateamos
		if (date.includes("T")) {
			return DateTime.fromISO(date).toFormat("yyyy-MM-dd");
		}

		// Para otros formatos de string, mantenemos la lógica original
		const dotIndex = date.indexOf(".");
		if (dotIndex !== -1) {
			return date.substring(0, dotIndex);
		}
		return date;
	}

	if (typeof date === "number") {
		return DateTime.fromMillis(date, { zone: "utc" }).toFormat("yyyy-MM-dd");
	}

	return "";
}

export function formatTimestampWithTime(
	date: string | null | undefined | number,
): string {
	if (date === null || date === undefined || date === "") {
		return "";
	}

	return formatTimestampWithFormat(date, "yyyy-MM-dd HH:mm:ss");
}

export function formatTimestampWithTimeForCard(
	date: string | null | undefined | number,
): string {
	if (date === null || date === undefined || date === "") {
		return "";
	}
	return formatTimestampWithFormat(date, "dd/MM/yy HH:mm");
}

function formatTimestampWithFormat(
	date: string | number,
	format: string,
): string {
	if (date === "") {
		return "";
	}
	if (typeof date === "string") {
		// Si es una fecha ISO (contiene T), parseamos y formateamos con hora
		if (date.includes("T")) {
			return DateTime.fromISO(date).toFormat(format);
		}

		// Para otros formatos de string, intentamos parsear como DateTime
		const parsed = DateTime.fromSQL(date);
		if (parsed.isValid) {
			return parsed.toFormat(format);
		}
		return date;
	}
	return DateTime.fromMillis(date, { zone: "utc" }).toFormat(format);
}

export function formatTimestampWithoutTime(
	date: string | null | undefined | number,
): string {
	if (date === null || date === undefined || date === "") {
		return "";
	}
	return formatTimestampWithFormat(date, "dd/MM/yy");
}

export function formatTimestampWithLocale(
	date: string | null | undefined | number,
	locale = "es",
	atTimeText = "a las",
): string {
	if (date === null || date === undefined || date === "") {
		return "";
	}

	let dateTime: DateTime;

	if (typeof date === "string") {
		// Si es una fecha ISO (contiene T), parseamos directamente
		if (date.includes("T")) {
			dateTime = DateTime.fromISO(date);
		} else {
			// Para otros formatos de string, intentamos parsear
			dateTime = DateTime.fromSQL(date);
			if (!dateTime.isValid) {
				dateTime = DateTime.fromISO(date);
			}
		}
	} else if (typeof date === "number") {
		dateTime = DateTime.fromMillis(date, { zone: "utc" });
	} else {
		return "";
	}

	if (!dateTime.isValid) {
		return "";
	}

	dateTime = dateTime.setLocale(locale);

	if (locale === "es") {
		// Formatear como "Jueves, 31 de julio de 2025 a las 09:34:23"
		const dayName = dateTime.toFormat("cccc");
		const day = dateTime.toFormat("d");
		const month = dateTime.toFormat("MMMM");
		const year = dateTime.toFormat("yyyy");
		const time = dateTime.toFormat("HH:mm:ss");

		const capitalizedDayName =
			dayName.charAt(0).toUpperCase() + dayName.slice(1);

		return `${capitalizedDayName}, ${day} de ${month} de ${year} ${atTimeText} ${time}`;
	}
	// Formatear en inglés como "Thursday, July 31, 2025 at 09:34:23"
	const dayName = dateTime.toFormat("cccc");
	const month = dateTime.toFormat("MMMM");
	const day = dateTime.toFormat("d");
	const year = dateTime.toFormat("yyyy");
	const time = dateTime.toFormat("HH:mm:ss");

	return `${dayName}, ${month} ${day}, ${year} ${atTimeText} ${time}`;
}

/**
 * Formatea el tamaño de archivo en bytes a una unidad legible
 */
export function formatFileSize(bytes: number, fractionDigits = 2): string {
	if (bytes === 0) return "0 B";
	const k = 1024;
	const sizes = ["B", "KB", "MB", "GB", "TB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return `${Number.parseFloat((bytes / k ** i).toFixed(fractionDigits))} ${sizes[i]}`;
}
